/**
 * StateManager - Game Phase State Machine
 * 
 * Manages the game's phase transitions:
 * MENU -> PLAYING -> SETTLING -> GAMEOVER
 *                                    |
 *         <---- (retry/menu) --------+
 * 
 * Also handles the "finishing phase" sub-state during PLAYING.
 */

import { DIFFICULTIES, DifficultySettings, TIMING } from './Constants';

/** Game phases */
export type GamePhase = 'menu' | 'playing' | 'settling' | 'gameover';

/** Difficulty levels */
export type Difficulty = 'easy' | 'normal' | 'hard';

/** Phase change callback */
export type PhaseChangeCallback = (newPhase: GamePhase, oldPhase: GamePhase) => void;

/** Finishing phase start callback */
export type FinishingStartCallback = () => void;

/** Timer tick callback (called each second during finishing phase) */
export type TimerTickCallback = (secondsLeft: number) => void;

/**
 * Current game state snapshot (for HUD and other systems)
 */
export interface GameStateSnapshot {
    phase: GamePhase;
    difficulty: Difficulty;
    difficultySettings: DifficultySettings;
    isFinishing: boolean;
    timeLeft: number | null;
}

/**
 * StateManager - Central game state controller
 */
export class StateManager {
    // Current state
    private phase: GamePhase = 'menu';
    private difficulty: Difficulty = 'normal';
    private isFinishing = false;
    private timeLeft: number | null = null;
    private settleStartTime = 0;
    
    // Timer tracking
    private lastTimerUpdate = 0;
    
    // Callbacks
    private phaseChangeCallbacks: PhaseChangeCallback[] = [];
    private finishingStartCallbacks: FinishingStartCallback[] = [];
    private timerTickCallbacks: TimerTickCallback[] = [];

    /**
     * Get current phase
     */
    getPhase(): GamePhase {
        return this.phase;
    }

    /**
     * Get current difficulty
     */
    getDifficulty(): Difficulty {
        return this.difficulty;
    }

    /**
     * Get current difficulty settings
     */
    getDifficultySettings(): DifficultySettings {
        return DIFFICULTIES[this.difficulty];
    }

    /**
     * Check if in finishing phase (countdown active)
     */
    getIsFinishing(): boolean {
        return this.isFinishing;
    }

    /**
     * Get time remaining (null if not in finishing phase)
     */
    getTimeLeft(): number | null {
        return this.timeLeft;
    }

    /**
     * Get a snapshot of current game state
     */
    getSnapshot(): GameStateSnapshot {
        return {
            phase: this.phase,
            difficulty: this.difficulty,
            difficultySettings: this.getDifficultySettings(),
            isFinishing: this.isFinishing,
            timeLeft: this.timeLeft,
        };
    }

    // ==================== CALLBACKS ====================

    /**
     * Register callback for phase changes
     */
    onPhaseChange(callback: PhaseChangeCallback): void {
        this.phaseChangeCallbacks.push(callback);
    }

    /**
     * Register callback for when finishing phase starts
     */
    onFinishingStart(callback: FinishingStartCallback): void {
        this.finishingStartCallbacks.push(callback);
    }

    /**
     * Register callback for timer ticks during finishing phase
     */
    onTimerTick(callback: TimerTickCallback): void {
        this.timerTickCallbacks.push(callback);
    }

    /**
     * Clear all callbacks
     */
    clearCallbacks(): void {
        this.phaseChangeCallbacks = [];
        this.finishingStartCallbacks = [];
        this.timerTickCallbacks = [];
    }

    // ==================== PHASE TRANSITIONS ====================

    /**
     * Start a new game with the given difficulty
     */
    startGame(difficulty: Difficulty): void {
        if (this.phase !== 'menu' && this.phase !== 'gameover') {
            console.warn(`Cannot start game from phase: ${this.phase}`);
            return;
        }

        const oldPhase = this.phase;
        this.phase = 'playing';
        this.difficulty = difficulty;
        this.isFinishing = false;
        this.timeLeft = null;
        this.lastTimerUpdate = 0;

        this.notifyPhaseChange(this.phase, oldPhase);
    }

    /**
     * Trigger the finishing phase (fill threshold reached)
     * Starts the countdown timer.
     */
    startFinishingPhase(): void {
        if (this.phase !== 'playing' || this.isFinishing) {
            return;
        }

        this.isFinishing = true;
        this.timeLeft = this.getDifficultySettings().finishingDuration;
        this.lastTimerUpdate = performance.now();

        // Notify listeners
        for (const callback of this.finishingStartCallbacks) {
            callback();
        }
    }

    /**
     * Transition to settling phase (timer hit 0)
     */
    startSettling(): void {
        if (this.phase !== 'playing') {
            console.warn(`Cannot start settling from phase: ${this.phase}`);
            return;
        }

        const oldPhase = this.phase;
        this.phase = 'settling';
        this.settleStartTime = performance.now();
        this.isFinishing = false;
        this.timeLeft = null;

        this.notifyPhaseChange(this.phase, oldPhase);
    }

    /**
     * Transition to game over (settling complete)
     */
    endGame(): void {
        if (this.phase !== 'settling') {
            console.warn(`Cannot end game from phase: ${this.phase}`);
            return;
        }

        const oldPhase = this.phase;
        this.phase = 'gameover';

        this.notifyPhaseChange(this.phase, oldPhase);
    }

    /**
     * Return to menu
     */
    returnToMenu(): void {
        const oldPhase = this.phase;
        this.phase = 'menu';
        this.isFinishing = false;
        this.timeLeft = null;

        this.notifyPhaseChange(this.phase, oldPhase);
    }

    /**
     * Retry with same difficulty
     */
    retry(): void {
        if (this.phase !== 'gameover') {
            console.warn(`Cannot retry from phase: ${this.phase}`);
            return;
        }

        this.startGame(this.difficulty);
    }

    // ==================== UPDATE ====================

    /**
     * Update timer and check for phase transitions.
     * Call this every frame during 'playing' and 'settling' phases.
     * 
     * @param now Current timestamp (performance.now())
     * @returns true if a phase transition occurred
     */
    update(now: number): boolean {
        // Handle finishing phase timer
        if (this.phase === 'playing' && this.isFinishing && this.timeLeft !== null) {
            const elapsed = now - this.lastTimerUpdate;
            
            if (elapsed >= 1000) {
                this.lastTimerUpdate = now;
                this.timeLeft--;

                // Notify timer tick
                for (const callback of this.timerTickCallbacks) {
                    callback(this.timeLeft);
                }

                // Check if timer expired
                if (this.timeLeft <= 0) {
                    this.startSettling();
                    return true;
                }
            }
        }

        // Handle settling phase completion
        if (this.phase === 'settling') {
            const settleElapsed = now - this.settleStartTime;
            if (settleElapsed >= TIMING.settleTime) {
                this.endGame();
                return true;
            }
        }

        return false;
    }

    /**
     * Get remaining settle time in milliseconds
     */
    getSettleTimeRemaining(now: number): number {
        if (this.phase !== 'settling') return 0;
        return Math.max(0, TIMING.settleTime - (now - this.settleStartTime));
    }

    // ==================== PRIVATE ====================

    private notifyPhaseChange(newPhase: GamePhase, oldPhase: GamePhase): void {
        for (const callback of this.phaseChangeCallbacks) {
            callback(newPhase, oldPhase);
        }
    }
}
